/**
 * The hh:mm pair shared by DateTimePicker and DateRangePicker.
 *
 * It holds the RAW TEXT, not the number. A controlled `value={hour}` fed by
 * `Number(e.target.value)` can never be emptied: clearing the box yields "",
 * `Number("")` is 0, and the field re-renders as "0" under the caret — so the
 * digits are undeletable and you can't type a new time, only prepend to the
 * old one. The text is the draft; the number is pushed up only once both
 * fields parse, and the boxes are normalised (clamped + zero-padded) on blur.
 */
import { useEffect, useState } from "react";
import { pad } from "./dateParts";
import styles from "./DateTimePicker.module.css";

const MAX = { h: 23, m: 59 };
type Field = "h" | "m";

const clamp = (n: number, max: number) => Math.max(0, Math.min(max, n || 0));

export function TimeFields({
  hour,
  minute,
  onChange,
  ariaPrefix,
  compact = false,
}: {
  hour: number;
  minute: number;
  onChange: (hour: number, minute: number) => void;
  /** Distinguishes the two pairs of a range ("Start" / "End") for a11y. */
  ariaPrefix?: string;
  /** Tighter boxes, for the two-pair range footer. */
  compact?: boolean;
}) {
  const [text, setText] = useState({ h: pad(hour), m: pad(minute) });
  const [editing, setEditing] = useState(false);

  // Props win whenever the user isn't mid-edit (re-picked day, reopen, reset).
  useEffect(() => {
    if (!editing) setText({ h: pad(hour), m: pad(minute) });
  }, [hour, minute, editing]);

  const push = (t: { h: string; m: string }) =>
    onChange(clamp(Number(t.h), MAX.h), clamp(Number(t.m), MAX.m));

  const edit = (field: Field, raw: string) => {
    const next = { ...text, [field]: raw.replace(/\D/g, "").slice(0, 2) };
    setText(next);
    // A half-typed pair stays local — nothing sane to push up yet.
    if (next.h === "" || next.m === "") return;
    push(next);
  };

  // Text is free-form while editing, so settle it: empty falls back to the
  // last good value, out-of-range clamps, everything zero-pads.
  const blur = () => {
    setEditing(false);
    const next = {
      h: pad(clamp(Number(text.h === "" ? hour : text.h), MAX.h)),
      m: pad(clamp(Number(text.m === "" ? minute : text.m), MAX.m)),
    };
    setText(next);
    push(next);
  };

  // Replaces the spinner we give up by leaving type="number"; wraps at both ends.
  const bump = (field: Field, delta: number) => {
    const max = MAX[field];
    const cur = clamp(Number(text[field] || 0), max);
    const next = { ...text, [field]: pad((cur + delta + max + 1) % (max + 1)) };
    setText(next);
    push(next);
  };

  const field = (f: Field, value: string, label: string) => (
    <input
      type="text"
      inputMode="numeric"
      maxLength={2}
      className={base(compact)}
      value={value}
      onChange={(e) => edit(f, e.target.value)}
      onFocus={(e) => {
        setEditing(true);
        e.target.select();
      }}
      onBlur={blur}
      onKeyDown={(e) => {
        if (e.key === "ArrowUp") {
          e.preventDefault();
          bump(f, 1);
        } else if (e.key === "ArrowDown") {
          e.preventDefault();
          bump(f, -1);
        }
      }}
      aria-label={ariaPrefix ? `${ariaPrefix} ${label}` : label}
    />
  );

  return (
    <span className={styles.timeFields}>
      {field("h", text.h, "hour")}
      <span className={styles.colon}>:</span>
      {field("m", text.m, "minute")}
    </span>
  );
}

const base = (compact: boolean) =>
  compact ? `${styles.timeInput} ${styles.timeInputCompact}` : styles.timeInput;
